"use client"

import {
  useProfile,
  buildSavedOpportunityFitProps,
  Card,
  CardContent,
  Button,
  PaneListSkeleton,
  PaneResponsiveCardGrid,
  PaneListCount,
  UnifiedListFilterBar,
  ListSortMenu,
  useArtifactCanvas,
  useUnifiedBrowseSearch,
  isOpportunityIngestEnabled,
  getListPagination,
  ListPagination,
} from '@yeshuman/sdk'
import { useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useOpportunities, type Opportunity } from '../../hooks/use-opportunities'
import { useSavedOpportunities } from '../../hooks/use-saved-opportunities'
import type { SavedOpportunity } from '../../hooks/use-saved-opportunities'
import { buildApplicationStatusMap, useMyApplications } from '../../hooks/use-my-applications'
import { CandidateOpportunityJobCard } from './candidate-opportunity-job-card'
import { ChevronDown, RefreshCw, Search } from 'lucide-react'
import { useNavigateToCandidateJobDetail } from '../../hooks/use-navigate-to-candidate-job-detail'
import { openApplicationApplyCanvas } from './open-application-apply-canvas'
import { hasActiveApplicationForOpportunity } from './job-application-trailing'
import {
  applyJobBrowseFiltersToSearchParams,
  DEFAULT_JOB_BROWSE_FILTERS,
  filterOpportunitiesBySelectedSkills,
  jobBrowseFiltersToOpportunitiesFilters,
  matchedSkillKeysForOpportunity,
  parseCandidateJobsSort,
  parseJobBrowseFiltersFromSearchParams,
} from '../../lib/job-browse-filters'
import { useJobSkillFacetHarvest } from '../../hooks/use-job-skill-facet-harvest'
import { getCandidateJobsFilterDefinitions } from '../../lib/list-filters/candidate-jobs'
import { CANDIDATE_JOBS_SORT_OPTIONS } from '../../lib/list-filters/candidate-jobs-constants'
import {
  CANDIDATE_JOB_FILTER_COVERAGE_HINT,
  shouldShowCandidateJobFilterCoverageHint,
} from '../../lib/list-filters/candidate-jobs-coverage'

const candidateBrowsePagination = getListPagination('candidateBrowse')

export function BrowseOpportunities() {
  const handleOpenJob = useNavigateToCandidateJobDetail()
  const { profile } = useProfile()
  const [searchParams, setSearchParams] = useSearchParams()
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const applyCanvasKeyRef = useRef(0)
  const filterDefinitions = useMemo(() => getCandidateJobsFilterDefinitions(), [])

  const filters = useMemo(() => {
    const parsed = parseJobBrowseFiltersFromSearchParams(searchParams)
    return {
      ...parsed,
      page_size: candidateBrowsePagination.enabled
        ? candidateBrowsePagination.pageSize
        : parsed.page_size,
    }
  }, [searchParams])

  const updateFilters = useCallback(
    (next: typeof filters) => {
      const base = new URLSearchParams(searchParams)
      setSearchParams(applyJobBrowseFiltersToSearchParams(base, next), { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const apiFilters = useMemo(
    () => jobBrowseFiltersToOpportunitiesFilters(filters),
    [filters],
  )

  const { opportunities: rawOpportunities, pagination, isLoading, error } = useOpportunities(apiFilters)
  const { skillFacets, skillFacetsLoading } = useJobSkillFacetHarvest()
  const { savedIds, pendingOpportunityIds, toggleSave, savingOpportunityId } =
    useSavedOpportunities()

  const opportunities = useMemo(
    () => filterOpportunitiesBySelectedSkills(rawOpportunities, filters),
    [rawOpportunities, filters],
  )

  const filterCtx = useMemo(
    () => ({
      skillFacets,
      skillFacetsLoading,
      selectedSkillLabels: filters.selectedSkills,
      showSourceFilter: isOpportunityIngestEnabled(),
    }),
    [skillFacets, skillFacetsLoading, filters.selectedSkills],
  )

  const showCoverageHint = useMemo(
    () => !isLoading && shouldShowCandidateJobFilterCoverageHint(filters),
    [filters, isLoading],
  )

  const { applications, refetch: refetchApplications } = useMyApplications({
    enabled: !!profile?.id,
  })

  const applicationStatusMap = useMemo(
    () => buildApplicationStatusMap(applications),
    [applications],
  )

  const handleApplyClick = useCallback(
    (opportunity: Opportunity) => {
      if (hasActiveApplicationForOpportunity(applicationStatusMap, opportunity.id)) return
      // Marketplace apply is off-platform (or alpha-captured) on the detail page — not ATS canvas.
      if (opportunity.kind === 'marketplace') {
        handleOpenJob(opportunity)
        return
      }
      openApplicationApplyCanvas({
        opportunity,
        openCanvas,
        closeCanvas,
        applyCanvasKeyRef,
        onApplied: () => void refetchApplications(),
        keyPrefix: 'browse-apply',
      })
    },
    [applicationStatusMap, closeCanvas, handleOpenJob, openCanvas, refetchApplications],
  )

  const handleSearch = useCallback(
    (patch: Partial<typeof filters>) => {
      updateFilters({ ...filters, ...patch, page: 1 })
    },
    [filters, updateFilters],
  )

  const { commitTextQuery, clearCommittedTextQuery } = useUnifiedBrowseSearch({
    committedQ: filters.q,
    onUpdateQ: (q) => handleSearch({ q }),
  })

  const clearAllFilters = useCallback(() => {
    updateFilters(DEFAULT_JOB_BROWSE_FILTERS)
  }, [updateFilters])

  const hasActiveFilters = useMemo(
    () =>
      filters.sort !== 'updated_desc' ||
      filterDefinitions.some((def) => def.isNonDefault(filters, filterCtx)) ||
      filters.q.trim().length > 0,
    [filters, filterDefinitions, filterCtx],
  )

  const sortMenu = (
    <ListSortMenu
      options={CANDIDATE_JOBS_SORT_OPTIONS}
      value={filters.sort}
      onValueChange={(v) => updateFilters({ ...filters, sort: parseCandidateJobsSort(v), page: 1 })}
      ariaLabel="Sort jobs"
    />
  )

  const handleLoadMore = () => {
    if (pagination.has_next) {
      updateFilters({ ...filters, page: filters.page + 1 })
    }
  }

  // Skills OR-match is client-only (not sent to the API). Numbered pagination would
  // lie about totals across pages, so fall back to Load More while skills are selected.
  const clientSkillFilterActive = filters.selectedSkills.length > 0
  const showBrowsePagination =
    candidateBrowsePagination.enabled &&
    !clientSkillFilterActive &&
    pagination.total > filters.page_size
  const listCount = clientSkillFilterActive ? opportunities.length : pagination.total

  const handleSaveToggle = useCallback(
    (opportunityId: string) => {
      toggleSave(opportunityId, savedIds.has(opportunityId))
    },
    [savedIds, toggleSave],
  )

  const matchedSkillKeysByOpportunityId = useMemo(() => {
    if (filters.selectedSkills.length === 0) {
      return new Map<string, string[]>()
    }
    return new Map(
      opportunities.map((opportunity) => [
        opportunity.id,
        matchedSkillKeysForOpportunity(opportunity, filters.selectedSkills),
      ]),
    )
  }, [filters.selectedSkills, opportunities])

  return (
    <div className="space-y-6">
      <UnifiedListFilterBar
        listId="candidateJobs"
        filters={filters}
        onFiltersChange={updateFilters}
        definitions={filterDefinitions}
        ctx={filterCtx}
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        toolbar={{ sortMenu }}
        search={{
          searchId: 'browse-opportunities-search',
          searchPlaceholder: 'Search or add filters…',
          keywordDisplay: 'inline',
          onSearchBlur: commitTextQuery,
          committedSearchValue: filters.q,
          onClearCommittedSearch: clearCommittedTextQuery,
        }}
      />

      {showCoverageHint ? (
        <p className="text-xs text-muted-foreground">{CANDIDATE_JOB_FILTER_COVERAGE_HINT}</p>
      ) : null}

      {isLoading && opportunities.length === 0 ? (
        <PaneListSkeleton count={4} ariaLabel="Searching opportunities" />
      ) : error ? (
        <Card className="w-full">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <h3 className="text-lg font-medium mb-2 text-destructive">Error Loading Opportunities</h3>
            <p className="text-muted-foreground text-center">{error.message}</p>
          </CardContent>
        </Card>
      ) : opportunities.length === 0 ? (
        <Card className="w-full">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Search className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {clientSkillFilterActive && pagination.has_next
                ? 'No matching jobs on this page'
                : 'No Opportunities Found'}
            </h3>
            <p className="text-muted-foreground text-center">
              {clientSkillFilterActive && pagination.has_next
                ? 'Skill filters apply to the jobs already loaded. Load more or clear skills to keep looking.'
                : 'Try adjusting your search criteria to find more opportunities.'}
            </p>
            {hasActiveFilters ? (
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                {clientSkillFilterActive && pagination.has_next ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleLoadMore}
                    disabled={isLoading}
                  >
                    Load more
                  </Button>
                ) : null}
                <Button type="button" variant="outline" size="sm" onClick={clearAllFilters}>
                  Clear all filters
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          <PaneListCount
            count={listCount}
            unit={{ singular: 'opportunity', plural: 'opportunities' }}
          />
          <PaneResponsiveCardGrid>
            {opportunities.map((opportunity) => {
              const isPending = pendingOpportunityIds.has(opportunity.id)
              const fitProps = isPending
                ? buildSavedOpportunityFitProps(
                    {
                      ...opportunity,
                      is_available: true,
                      interest_at: null,
                      insight: null,
                      insight_pending: true,
                    } as SavedOpportunity,
                    { pending: true },
                  )
                : undefined
              return (
                <CandidateOpportunityJobCard
                  key={opportunity.id}
                  className="h-full"
                  opportunity={opportunity}
                  application={applicationStatusMap[opportunity.id]}
                  saved={savedIds.has(opportunity.id)}
                  isSaving={savingOpportunityId === opportunity.id}
                  fitProps={fitProps}
                  matchedSkillKeys={
                    filters.selectedSkills.length > 0
                      ? matchedSkillKeysByOpportunityId.get(opportunity.id)
                      : undefined
                  }
                  onOpenJob={handleOpenJob}
                  onSaveToggle={handleSaveToggle}
                  onApply={handleApplyClick}
                />
              )
            })}
          </PaneResponsiveCardGrid>

          {showBrowsePagination ? (
            <ListPagination
              page={filters.page}
              pageSize={filters.page_size}
              total={pagination.total}
              onPageChange={(nextPage) => updateFilters({ ...filters, page: nextPage })}
            />
          ) : pagination.has_next ? (
            <div className="text-center pt-4">
              <Button
                variant="outline"
                onClick={handleLoadMore}
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                {isLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                Load More Opportunities
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
